import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.website";
const FAVICON_URL = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png";

export default function SafetyGuidePage() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Kakobuy Safety Guide: How to Avoid Scams Using Our Spreadsheet 2026",
    "description": "Protect yourself from common Kakobuy scams with our comprehensive safety guide featuring red flag checklists, seller verification tips, and secure payment practices from our trusted spreadsheet.",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Team"
    },
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": SITE_URL + "/guide/safety-guide"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": FAVICON_URL
      }
    },
    "image": "https://readdy.ai/api/search-image?query=A%20shield%20icon%20and%20a%20checklist%20on%20a%20laptop%20screen%20placed%20on%20a%20clean%20minimalist%20desk%20beside%20a%20locked%20padlock%20and%20a%20credit%20card%20soft%20natural%20lighting%20from%20a%20window%20creating%20a%20sense%20of%20security%20and%20trust%20editorial%20photography%20with%20clean%20composition%20and%20professional%20atmosphere&width=1400&height=600&seq=article-safety-v2-08&orientation=landscape"
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-6 cursor-pointer">
              <i className="ri-arrow-left-line text-sm"></i>Back to Kakobuy Spreadsheet Home
            </Link>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">Safety Guide</span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 text-balance">
              <span className="text-primary-500">Kakobuy Safety</span> Guide: How to Avoid Scams Using Our Spreadsheet
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span>Last Updated: June 25, 2026</span><span className="w-1 h-1 rounded-full bg-foreground-400"></span><span>5 min read</span>
            </div>
          </div>

          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-8">
              <img src="https://readdy.ai/api/search-image?query=A%20shield%20icon%20and%20a%20checklist%20on%20a%20laptop%20screen%20placed%20on%20a%20clean%20minimalist%20desk%20beside%20a%20locked%20padlock%20and%20a%20credit%20card%20soft%20natural%20lighting%20from%20a%20window%20creating%20a%20sense%20of%20security%20and%20trust%20editorial%20photography%20with%20clean%20composition%20and%20professional%20atmosphere&width=1400&height=600&seq=article-safety-v2-08&orientation=landscape"
                alt="Kakobuy Safety Guide" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Shopping Safely with Kakobuy Spreadsheet</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Safety is the top priority for every <strong>Kakobuy Spreadsheet</strong> user. While the vast majority of sellers on the platform are legitimate, a small percentage of bad actors exist — just like on any online marketplace. Our spreadsheet has already filtered out the most common risks, but understanding the safety landscape helps you shop with complete confidence.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                The <strong>Kakobuy Spreadsheet</strong> includes a safety rating column that flags listings with verified transaction history, responsive seller communication, and consistent product quality. Items without these verification markers should be approached with extra caution, and our team continuously monitors seller behavior to keep the spreadsheet up to date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Red Flags to Watch For</h2>
              <div className="space-y-3">
                {[
                  { flag: "No QC Photos Available", desc: "Legitimate sellers in the Kakobuy Spreadsheet always provide clear QC images. If a listing lacks QC photos, it is a major red flag regardless of the seller's claimed rating." },
                  { flag: "Prices Too Good to Be True", desc: "If a product is priced significantly below the average in our spreadsheet for that category, it often indicates a bait-and-switch or counterfeit item." },
                  { flag: "New Seller With No History", desc: "Brand new sellers without any transaction history should be avoided until they have built a track record. The spreadsheet only includes sellers with verifiable history." },
                  { flag: "Pressure to Pay Outside Platform", desc: "Any seller asking you to complete payment outside of Kakobuy is attempting to bypass buyer protection. Report these immediately and never pay through unofficial channels." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-xl bg-red-50 border border-red-200/60">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-red-500 text-base"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.flag}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-home-4-line text-sm"></i>Back to Homepage
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-200 text-foreground-700 text-sm font-medium rounded-lg hover:border-foreground-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Browse Spreadsheet <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}